import { useCallback, useEffect, useRef, useState } from 'react';
import { buscarCep, type EnderecoPorCep } from '../services/CepService';

/* Preenche o endereço quando o CEP fica completo — Organizações e Contatos.

   Uso:

     const cep = useBuscaCep(e => setRascunho(r => ({ ...r, cidade: e.cidade, ... })));
     <input value={rascunho.cep} onChange={ev => { setCep(ev); cep.aoDigitar(ev.target.value); }} />
     {cep.aviso && <p className={cep.aviso.erro ? 'rp-error' : 'rp-hint'}>{cep.aviso.texto}</p>}

   Busca só no evento de digitar, nunca ao abrir o registro: abrir para editar
   não pode reescrever o endereço que já estava gravado.

   Quem decide O QUE sobrescrever é a tela, no `aoAchar` — mas a regra das
   duas é a mesma: rua e bairro vêm da BrasilAPI quando ela devolve; quando
   não devolve (CEP de cidade inteira), o que estava fica só se a cidade não
   mudou — ver `manterSeMesmaCidade`.

   Trava (`travado`): enquanto busca, os quatro campos que a busca preenche
   ficam desabilitados; quando ela volta, seguem travados só os que ela achou.
   Não achou nada, ou a BrasilAPI caiu, abre tudo. Apagar um dígito do CEP
   também abre: é o caminho para corrigir um endereço que veio errado.
   Número e complemento nunca travam — a BrasilAPI não sabe. */

type Situacao = 'ocioso' | 'buscando' | 'sem-rua' | 'nao-encontrado' | 'indisponivel';

export type CampoDoCep = 'logradouro' | 'bairro' | 'cidade' | 'estado';
const TODOS: CampoDoCep[] = ['logradouro', 'bairro', 'cidade', 'estado'];

const AVISOS: Record<Exclude<Situacao, 'ocioso'>, { texto: string; erro: boolean }> = {
  buscando: { texto: 'Buscando o endereço…', erro: false },
  /* Curtos de propósito: o CEP ocupa meia coluna do painel, e aviso que
     quebra em duas linhas empurra a linha da rua para baixo. */
  'sem-rua': { texto: 'CEP da cidade toda: informe a rua.', erro: false },
  'nao-encontrado': { texto: 'CEP não encontrado.', erro: true },
  indisponivel: { texto: 'Busca de CEP fora do ar agora.', erro: false },
};

/* `aoAchar` pode devolver o id do campo que recebe o foco. Quem foca é o
   gancho, DEPOIS do commit: o campo pode estar travado até este render, e
   foco em campo desabilitado não pega. `requestAnimationFrame` não bastava —
   às vezes rodava antes do React aplicar o render (medido em 26/09). */
export function useBuscaCep(aoAchar: (endereco: EnderecoPorCep) => string | void) {
  const [situacao, setSituacao] = useState<Situacao>('ocioso');
  const [travados, setTravados] = useState<CampoDoCep[]>([]);

  /* O último CEP buscado: redigitar o mesmo número não busca de novo. */
  const ultimo = useRef('');
  const controle = useRef<AbortController | null>(null);

  /* Em ref para o `aoDigitar` não mudar de identidade a cada render da tela. */
  const aoAcharRef = useRef(aoAchar);
  aoAcharRef.current = aoAchar;

  useEffect(() => () => controle.current?.abort(), []);

  const focarDepois = useRef<string | null>(null);
  useEffect(() => {
    if (!focarDepois.current) return;
    document.getElementById(focarDepois.current)?.focus();
    focarDepois.current = null;
  }, [travados]);

  const aoDigitar = useCallback((valor: string) => {
    const digitos = valor.replace(/\D/g, '').slice(0, 8);

    if (digitos.length < 8) {
      controle.current?.abort();
      ultimo.current = '';
      setSituacao('ocioso');
      setTravados([]);
      return;
    }
    if (digitos === ultimo.current) return;

    /* Cancela a busca anterior: sem isso, uma resposta lenta de um CEP
       digitado antes chegaria por último e ganharia. */
    controle.current?.abort();
    const atual = new AbortController();
    controle.current = atual;
    ultimo.current = digitos;
    setSituacao('buscando');
    setTravados(TODOS);

    buscarCep(digitos, atual.signal)
      .then(resultado => {
        if (atual.signal.aborted) return;
        if (resultado.tipo !== 'achado') {
          setSituacao(resultado.tipo);
          setTravados([]);
          return;
        }
        const e = resultado.endereco;
        setSituacao(e.logradouro ? 'ocioso' : 'sem-rua');
        focarDepois.current = aoAcharRef.current(e) || null;
        setTravados(TODOS.filter(c => !!e[c]));
      })
      .catch(() => { /* cancelada: a busca seguinte é quem responde */ });
  }, []);

  /* Ao abrir outro registro ou fechar o painel. */
  const reiniciar = useCallback(() => {
    controle.current?.abort();
    ultimo.current = '';
    setSituacao('ocioso');
    setTravados([]);
  }, []);

  const aviso = situacao === 'ocioso' ? null : AVISOS[situacao];
  const travado = (campo: CampoDoCep) => travados.includes(campo);

  return { aviso, buscando: situacao === 'buscando', travado, aoDigitar, reiniciar };
}
